import * as ImagePicker from 'expo-image-picker';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronLeft,
  Plus,
  Trash2,
} from 'lucide-react-native';
import { ComponentType, useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { DocumentsList } from '@/components/system/DocumentsList';
import { ServiceHistoryList } from '@/components/system/ServiceHistoryList';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  CATEGORY_LABEL,
  getSystemMeta,
  resolveSystemDisplay,
  type ResolvedSystem,
} from '@/data/systems';
import type { SystemReminderRow, SystemRow } from '@/db/types';
import {
  deleteSystem,
  getActiveProperty,
  getSystemById,
  regenerateTasksForProperty,
  updateSystem,
} from '@/lib/tasks';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import { persistPickedFile } from '@/lib/files';
import { parseExtraPhotos } from '@/lib/inventory';
import { goBackOrTo } from '@/lib/nav';
import {
  cancelSystemReminder,
  cancelTaskReminder,
  scheduleSystemReminder,
} from '@/lib/notifications';
import { suggestReplacementReminder } from '@/lib/system-defaults';
import {
  addSystemReminder,
  cancelAllForSystem,
  deleteSystemReminder,
  listSystemReminders,
} from '@/lib/system-reminders';
import { resolveIcon } from '@/lib/rule-icon';
import { MONTH_OPTIONS, defaultSeasonalRange, seasonalLabel } from '@/lib/seasonal';
import { webNoOutline } from '@/lib/web-style';
import { StickyBottomBar } from '@/components/StickyBottomBar';

export default function EditSystem() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const notice = useNotice();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [row, setRow] = useState<SystemRow | null>(null);
  const [subtype, setSubtype] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [model, setModel] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [installYear, setInstallYear] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [extraPhotos, setExtraPhotos] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [reminderAt, setReminderAt] = useState<number | null>(null);
  const [reminderLabel, setReminderLabel] = useState<string>('');
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false);
  const [multiReminders, setMultiReminders] = useState<SystemReminderRow[]>([]);
  const [newReminderLabel, setNewReminderLabel] = useState('');
  const [newReminderAt, setNewReminderAt] = useState<number | null>(null);
  const [newReminderPickerOpen, setNewReminderPickerOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const reloadMultiReminders = async () => {
    if (!id) return;
    setMultiReminders(await listSystemReminders(db, id));
  };
  const [isActive, setIsActive] = useState(true);
  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [seasonalOn, setSeasonalOn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      const r = await getSystemById(db, id);
      if (cancelled) return;
      setRow(r);
      if (r) {
        setSubtype(r.subtype);
        setNickname(r.nickname ?? '');
        setModel(r.model ?? '');
        setFilterSize(r.filter_size ?? '');
        setInstallYear(r.installed_year ? String(r.installed_year) : '');
        setNotes(r.notes ?? '');
        setPhoto(r.photo_uri);
        setExtraPhotos(parseExtraPhotos(r.extra_photos_json));
        setReminderAt(r.reminder_at);
        setReminderLabel(r.reminder_label ?? '');
        setMultiReminders(await listSystemReminders(db, r.id));
        setIsActive(r.is_active === 1);
        setStartMonth(r.active_start_month);
        setEndMonth(r.active_end_month);
        setSeasonalOn(r.active_start_month != null && r.active_end_month != null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db, id]);

  if (!row) return <View style={styles.container} />;

  const display: ResolvedSystem = resolveSystemDisplay(row);
  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> =
    resolveIcon(display.icon);

  const pickPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `system-${Date.now()}.jpg`
    );
    setPhoto(persisted);
  };

  const addExtraPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      allowsMultipleSelection: true,
    });
    if (res.canceled || !res.assets.length) return;
    const next: string[] = [...extraPhotos];
    for (const a of res.assets) {
      const persisted = await persistPickedFile(
        a.uri,
        a.fileName ?? `system-${Date.now()}.jpg`
      );
      next.push(persisted);
    }
    setExtraPhotos(next);
  };

  const removeExtra = (i: number) => {
    setExtraPhotos((arr) => arr.filter((_, idx) => idx !== i));
  };

  const promoteExtra = (i: number) => {
    // Make this extra photo the primary; demote old primary into extras so
    // nothing is lost.
    const newPrimary = extraPhotos[i];
    const remaining = extraPhotos.filter((_, idx) => idx !== i);
    if (photo) remaining.unshift(photo);
    setPhoto(newPrimary);
    setExtraPhotos(remaining);
  };

  const save = async () => {
    setWorking(true);
    try {
      const trimmedReminderLabel = reminderLabel.trim();
      const finalReminderAt = reminderAt && trimmedReminderLabel ? reminderAt : null;
      const finalReminderLabel = finalReminderAt ? trimmedReminderLabel : null;
      await updateSystem(db, row.id, {
        subtype: subtype || null,
        nickname: nickname.trim() || null,
        model: display.hasModel ? model.trim() || null : null,
        filter_size: display.hasFilter ? filterSize.trim() || null : null,
        installed_year: installYear ? parseInt(installYear, 10) || null : null,
        photo_uri: photo,
        extra_photos: extraPhotos,
        notes: notes.trim() || null,
        is_active: isActive ? 1 : 0,
        active_start_month: seasonalOn ? startMonth : null,
        active_end_month: seasonalOn ? endMonth : null,
        reminder_at: finalReminderAt,
        reminder_label: finalReminderLabel,
      });
      // Update the OS-side reminder to match. Skip the schedule call
      // entirely when the user has turned off System Reminders
      // globally in Settings → Notifications. The DB still saves the
      // date so they can see it on this page; we just don't tell the
      // OS to ping. Toggling the global setting back on will re-arm
      // existing entries on the next save.
      const propForReminder = await getActiveProperty(db);
      const systemRemindersOn =
        (propForReminder?.notify_system_reminders ?? 1) === 1;
      if (finalReminderAt && finalReminderLabel && systemRemindersOn) {
        await scheduleSystemReminder(row.id, finalReminderLabel, finalReminderAt);
      } else {
        await cancelSystemReminder(row.id);
      }
      await regenerateTasksForProperty(db, row.property_id);
      goBackOrTo(router, '/(tabs)/my-home');
    } catch (err) {
      console.warn('updateSystem failed', err);
      const msg = err instanceof Error ? err.message : String(err);
      void notice({ title: "Couldn't save changes", message: msg, tone: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const toggleSeasonal = () => {
    if (seasonalOn) {
      setSeasonalOn(false);
    } else {
      const def = defaultSeasonalRange(row.kind);
      setStartMonth(def.startMonth ?? 5);
      setEndMonth(def.endMonth ?? 9);
      setSeasonalOn(true);
    }
  };

  const remove = async () => {
    setWorking(true);
    try {
      // Cancel any OS reminders attached to this system's tasks before the
      // tasks get soft-deleted, so the user doesn't get pinged about a task
      // belonging to a system that no longer exists.
      const linked = await db.getAllAsync<{ id: string }>(
        `SELECT id FROM task WHERE system_id = ? AND completed_at IS NULL AND deleted_at IS NULL`,
        row.id
      );
      await Promise.all(linked.map((t) => cancelTaskReminder(t.id)));
      await cancelSystemReminder(row.id);
      await cancelAllForSystem(db, row.id);
      await deleteSystem(db, row.id);
      await regenerateTasksForProperty(db, row.property_id);
      goBackOrTo(router, '/(tabs)/my-home');
    } finally {
      setWorking(false);
    }
  };

  const confirmRemove = async () => {
    const ok = await confirm({
      title: 'Remove system?',
      message: `${display.label} and its scheduled tasks will be removed.`,
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (ok) await remove();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/my-home')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Edit system</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        <View style={styles.head}>
          <View style={styles.headIco}>
            <Icon size={24} color={Palette.forest} strokeWidth={1.7} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.headTitle}>{display.label}</Text>
            <Text style={styles.headSub}>
              {display.location === 'inside' ? 'Inside' : 'Outside'} · {CATEGORY_LABEL[display.category]}
              {display.isCustom ? ' · Custom' : ''}
            </Text>
          </View>
        </View>

        {display.subtypes && display.subtypes.length > 0 ? (
          <View style={styles.section}>
            <View style={styles.subtypeHead}>
              <Text style={styles.sectionTitle}>TYPE</Text>
              {/*
                When the user hasn't picked a subtype yet, show the same
                "Needs tune" badge that appears on the /systems page
                row. Reinforces what this missing field unlocks and
                visually rhymes across the system surfaces.
              */}
              {!subtype ? (
                <View style={styles.tuneBadge}>
                  <Text style={styles.tuneBadgeText}>Needs tune</Text>
                </View>
              ) : null}
            </View>
            {!subtype ? (
              <Text style={styles.subtypeHint}>
                Pick a type to tune the tasks scheduled for this {display.label.toLowerCase()}.
              </Text>
            ) : null}
            <View style={{ gap: 8 }}>
              {display.subtypes.map((s) => {
                const sel = subtype === s.key;
                return (
                  <Pressable
                    key={s.key}
                    // Tap a selected row to deselect — gives the user a
                    // way to undo a wrong pick without an extra clear
                    // button. Matches the multi-select chip pattern
                    // used on the onboarding Detail screen.
                    onPress={() => setSubtype(sel ? null : s.key)}
                    style={({ pressed }) => [
                      styles.subRow,
                      sel && styles.subRowSel,
                      pressed && { opacity: 0.94 },
                    ]}>
                    <View style={[styles.radio, sel && styles.radioSel]}>
                      {sel ? <Check size={12} color={Palette.cream} strokeWidth={2.5} /> : null}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.subTitle}>{s.label}</Text>
                      <Text style={styles.subDesc}>{s.description}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>STATUS</Text>
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.toggleLabel}>{isActive ? 'Active' : 'Disabled'}</Text>
              <Text style={styles.toggleSub}>
                {isActive
                  ? 'Tasks for this system will appear in your plan.'
                  : 'Tasks are paused. Re-enable to bring them back.'}
              </Text>
            </View>
            <Pressable
              onPress={() => setIsActive(!isActive)}
              style={({ pressed }) => [
                styles.toggle,
                isActive && styles.toggleOn,
                pressed && { opacity: 0.85 },
              ]}>
              <View style={[styles.toggleThumb, isActive && styles.toggleThumbOn]} />
            </Pressable>
          </View>

          <View style={[styles.toggleRow, { marginTop: 10 }]}>
            <View style={{ flex: 1 }}>
              <Text style={styles.toggleLabel}>Seasonal</Text>
              <Text style={styles.toggleSub}>
                {seasonalOn
                  ? seasonalLabel({ startMonth, endMonth }) ?? 'Pick months'
                  : 'Year-round — tasks any time'}
              </Text>
            </View>
            <Pressable
              onPress={toggleSeasonal}
              style={({ pressed }) => [
                styles.toggle,
                seasonalOn && styles.toggleOn,
                pressed && { opacity: 0.85 },
              ]}>
              <View style={[styles.toggleThumb, seasonalOn && styles.toggleThumbOn]} />
            </Pressable>
          </View>

          {seasonalOn ? (
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
              <MonthPicker
                label="STARTS"
                value={startMonth}
                onChange={setStartMonth}
              />
              <MonthPicker
                label="ENDS"
                value={endMonth}
                onChange={setEndMonth}
              />
            </View>
          ) : null}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>DETAILS</Text>
          <Field label="Nickname" value={nickname} onChangeText={setNickname} placeholder="e.g. Living-room fireplace" />
          {display.hasModel ? (
            <Field label="Model number" value={model} onChangeText={setModel} placeholder="From the data plate" />
          ) : null}
          {display.hasFilter ? (
            <Field label="Filter size" value={filterSize} onChangeText={setFilterSize} placeholder="e.g. 16 × 25 × 1" />
          ) : null}
          <Field
            label="Installed (year)"
            value={installYear}
            onChangeText={setInstallYear}
            placeholder="e.g. 2016"
            inputMode="numeric"
          />

          <Pressable onPress={pickPhoto} style={styles.photoButton}>
            {photo ? (
              <Image source={{ uri: photo }} style={styles.photo} />
            ) : (
              <>
                <Camera size={22} color={Palette.inkFaint} strokeWidth={1.7} />
                <Text style={styles.photoLabel}>Add a photo</Text>
              </>
            )}
          </Pressable>

          <Text style={[styles.fieldLabel, { marginTop: Spacing.three }]}>GALLERY</Text>
          <View style={styles.galleryRow}>
            {extraPhotos.map((uri, i) => (
              <View key={`${uri}-${i}`} style={styles.galleryThumbWrap}>
                <Pressable
                  onPress={() => setLightboxIndex(i)}
                  onLongPress={() => promoteExtra(i)}
                  delayLongPress={350}
                  style={styles.galleryThumbPress}>
                  <Image source={{ uri }} style={styles.galleryThumb} />
                </Pressable>
                <Pressable onPress={() => removeExtra(i)} style={styles.galleryRemove} hitSlop={6}>
                  <Text style={styles.galleryRemoveText}>×</Text>
                </Pressable>
              </View>
            ))}
            <Pressable onPress={addExtraPhoto} style={styles.galleryAdd}>
              <Plus size={18} color={Palette.forest} strokeWidth={2} />
              <Text style={styles.galleryAddText}>Add</Text>
            </Pressable>
          </View>
          {extraPhotos.length > 0 ? (
            <Text style={styles.galleryHint}>Tap to view · long-press to make primary · × to remove.</Text>
          ) : null}

          <Text style={[styles.fieldLabel, { marginTop: Spacing.three }]}>NOTES</Text>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Anything you want future-you to know about it"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, webNoOutline, { height: 88, textAlignVertical: 'top' }]}
          />

          <Text style={[styles.fieldLabel, { marginTop: Spacing.three }]}>REMINDER · OPTIONAL</Text>
          <Text style={styles.reminderHelp}>
            One-shot ping for things like insurance renewal, warranty
            expiry, or a service-contract end date.
          </Text>
          {(() => {
            // Smart suggestion: only show when we have install year + no
            // reminder set yet + we recognize the system kind.
            const yr = installYear ? parseInt(installYear, 10) : null;
            const sug = !reminderAt && yr ? suggestReplacementReminder(row.kind, yr) : null;
            if (!sug) return null;
            return (
              <Pressable
                onPress={() => {
                  setReminderAt(sug.at);
                  setReminderLabel(sug.label);
                }}
                style={({ pressed }) => [styles.suggestion, pressed && { opacity: 0.85 }]}>
                <Text style={styles.suggestionEyebrow}>SUGGESTED</Text>
                <Text style={styles.suggestionTitle}>{sug.label}</Text>
                <Text style={styles.suggestionDate}>
                  {new Date(sug.at).toLocaleDateString(undefined, {
                    month: 'long',
                    year: 'numeric',
                  })}{' '}
                  · tap to use
                </Text>
              </Pressable>
            );
          })()}
          <TextInput
            value={reminderLabel}
            onChangeText={setReminderLabel}
            placeholder="e.g. Renew home insurance"
            placeholderTextColor={Palette.inkFaint}
            style={[styles.fieldInput, webNoOutline]}
          />
          <Pressable
            onPress={() => setReminderPickerOpen(true)}
            style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }, { marginTop: 8 }]}>
            <Text style={[styles.pickText, !reminderAt && styles.pickPlaceholder]}>
              {reminderAt
                ? new Date(reminderAt).toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'Tap to pick a date'}
            </Text>
            <Text style={styles.pickPlaceholder}>
              {reminderAt ? 'Change' : ''}
            </Text>
          </Pressable>
          {reminderAt ? (
            <Pressable
              onPress={() => {
                setReminderAt(null);
                setReminderLabel('');
              }}
              style={styles.clearReminder}>
              <Text style={styles.clearReminderText}>Clear reminder</Text>
            </Pressable>
          ) : null}

          {/* Extra reminders — list + inline add. Independent of the
              primary reminder above; saved immediately on add (not on
              "Save changes") so each row reflects DB state. */}
          {multiReminders.length > 0 ? (
            <>
              <Text style={[styles.fieldLabel, { marginTop: Spacing.four }]}>
                EXTRA REMINDERS · {multiReminders.length}
              </Text>
              {multiReminders.map((r) => (
                <View key={r.id} style={styles.multiRow}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.multiLabel} numberOfLines={1}>{r.label}</Text>
                    <Text style={styles.multiDate}>
                      {new Date(r.fire_at).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </Text>
                  </View>
                  <Pressable
                    onPress={async () => {
                      await deleteSystemReminder(db, r.id);
                      await reloadMultiReminders();
                    }}
                    style={({ pressed }) => [styles.multiRemove, pressed && { opacity: 0.7 }]}
                    hitSlop={6}
                    accessibilityLabel="Remove reminder">
                    <Text style={styles.multiRemoveText}>×</Text>
                  </Pressable>
                </View>
              ))}
            </>
          ) : null}

          <Text style={[styles.fieldLabel, { marginTop: Spacing.three }]}>ADD ANOTHER REMINDER</Text>
          <TextInput
            value={newReminderLabel}
            onChangeText={setNewReminderLabel}
            placeholder="e.g. AC tune-up appointment"
            placeholderTextColor={Palette.inkFaint}
            style={[styles.fieldInput, webNoOutline]}
          />
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            <Pressable
              onPress={() => setNewReminderPickerOpen(true)}
              style={({ pressed }) => [styles.pickBox, { flex: 1 }, pressed && { opacity: 0.92 }]}>
              <Text style={[styles.pickText, !newReminderAt && styles.pickPlaceholder]}>
                {newReminderAt
                  ? new Date(newReminderAt).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })
                  : 'Pick a date'}
              </Text>
            </Pressable>
            <Pressable
              onPress={async () => {
                if (!newReminderLabel.trim() || !newReminderAt) return;
                await addSystemReminder(db, {
                  systemId: row.id,
                  label: newReminderLabel.trim(),
                  fire_at: newReminderAt,
                });
                setNewReminderLabel('');
                setNewReminderAt(null);
                await reloadMultiReminders();
              }}
              disabled={!newReminderLabel.trim() || !newReminderAt}
              style={({ pressed }) => [
                styles.addReminderBtn,
                (!newReminderLabel.trim() || !newReminderAt) && { opacity: 0.4 },
                pressed && { transform: [{ scale: 0.97 }] },
              ]}>
              <Text style={styles.addReminderText}>+ Add</Text>
            </Pressable>
          </View>
        </View>

        {/* Save moved to a sticky bottom bar below — see end of return.
            Custom-task and Remove actions remain inline since they're
            secondary / destructive flows the user won't take by accident. */}
        <Pressable
          onPress={() => router.push(`/custom-task/${row.id}` as never)}
          style={({ pressed }) => [styles.customTaskBtn, pressed && { transform: [{ scale: 0.98 }] }]}>
          <Plus size={16} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.customTaskText}>Add a custom task for this system</Text>
        </Pressable>

        <DocumentsList propertyId={row.property_id} systemId={row.id} />
        <ServiceHistoryList systemId={row.id} />

        <Pressable onPress={confirmRemove} style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.85 }]}>
          <Trash2 size={16} color={Palette.clay} strokeWidth={1.8} />
          <Text style={styles.removeText}>Remove this system</Text>
        </Pressable>
      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={working}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1, marginTop: 0 },
            working && { opacity: 0.6 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{working ? 'Saving…' : 'Save changes'}</Text>
        </Pressable>
      </StickyBottomBar>

      <PhotoLightbox
        uris={lightboxIndex != null ? extraPhotos : null}
        startIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />

      <DatePickerSheet
        open={reminderPickerOpen}
        value={reminderAt}
        onPick={(ms) => {
          setReminderAt(ms);
          setReminderPickerOpen(false);
        }}
        onClose={() => setReminderPickerOpen(false)}
        title="Reminder date"
        minDate={Date.now()}
      />

      <DatePickerSheet
        open={newReminderPickerOpen}
        value={newReminderAt}
        onPick={(ms) => {
          setNewReminderAt(ms);
          setNewReminderPickerOpen(false);
        }}
        onClose={() => setNewReminderPickerOpen(false)}
        title="Reminder date"
        minDate={Date.now()}
      />
    </SafeAreaView>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        style={[styles.fieldInput, webNoOutline]}
      />
    </View>
  );
}

function MonthPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = MONTH_OPTIONS.find((m) => m.value === value);
  return (
    <View style={{ flex: 1 }}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Pressable
        onPress={() => setOpen(true)}
        style={({ pressed }) => [styles.monthBox, pressed && { opacity: 0.9 }]}>
        <Text style={styles.monthText}>{current?.label ?? '—'}</Text>
      </Pressable>
      <Modal animationType="fade" transparent visible={open} onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setOpen(false)}>
          <View style={styles.monthGrid}>
            <Text style={styles.modalTitle}>{label === 'STARTS' ? 'Start month' : 'End month'}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {MONTH_OPTIONS.map((m) => {
                const sel = m.value === value;
                return (
                  <Pressable
                    key={m.value}
                    onPress={() => {
                      onChange(m.value);
                      setOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.monthChip,
                      sel && styles.monthChipSel,
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text style={[styles.monthChipText, sel && styles.monthChipTextSel]}>
                      {m.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topbarTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  head: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: 16,
  },
  headIco: {
    width: 48,
    height: 48,
    borderRadius: 14,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 19,
    color: Palette.ink,
  },
  headSub: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 2,
  },
  section: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
    marginBottom: Spacing.three,
  },
  // Subtype section header row — accommodates the "Needs tune" badge
  // next to the TYPE label when the user hasn't picked yet.
  subtypeHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: Spacing.three,
  },
  tuneBadge: {
    paddingHorizontal: 8, paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.honeySoft,
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.45)',
    marginBottom: Spacing.three, // align with sectionTitle bottom margin
  },
  tuneBadgeText: {
    fontFamily: Fonts.sansBold, fontSize: 11,
    color: Palette.honeyDeep, letterSpacing: 0.4,
  },
  subtypeHint: {
    marginTop: -Spacing.two,
    marginBottom: Spacing.three,
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, lineHeight: 17,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  subRowSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: Palette.sandDeep,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  radioSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  subTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  subDesc: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 1,
    lineHeight: 17,
  },
  fieldLabel: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  photoButton: {
    width: '100%',
    height: 110,
    marginTop: Spacing.three,
    borderRadius: 14,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: Palette.sandDeep,
    backgroundColor: Palette.sand,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    overflow: 'hidden',
  },
  photo: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  photoLabel: {
    color: Palette.inkFaint,
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 16,
    borderRadius: 18,
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
  },
  saveText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 15.5,
  },
  removeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
  },
  removeText: {
    color: Palette.clay,
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  toggleLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  toggleSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 2,
    lineHeight: 17,
  },
  toggle: {
    width: 44,
    height: 26,
    borderRadius: 13,
    backgroundColor: Palette.sandDeep,
    padding: 3,
    justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Palette.cream,
  },
  toggleThumbOn: { transform: [{ translateX: 18 }] },
  monthBox: {
    marginTop: 5,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    alignItems: 'center',
  },
  monthText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    marginBottom: 10,
  },
  monthGrid: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
  },
  monthChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  monthChipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  monthChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  monthChipTextSel: { color: Palette.cream },
  customTaskBtn: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 13,
    borderRadius: 14,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  customTaskText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
  },
  galleryRow: {
    marginTop: 6,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  galleryThumbWrap: {
    position: 'relative',
  },
  galleryThumbPress: {
    width: 70,
    height: 70,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  galleryThumb: { width: '100%', height: '100%' },
  galleryRemove: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Palette.clay,
    alignItems: 'center',
    justifyContent: 'center',
  },
  galleryRemoveText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    lineHeight: 16,
  },
  galleryAdd: {
    width: 70,
    height: 70,
    borderRadius: 12,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  galleryAddText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.forest,
    letterSpacing: 0.4,
  },
  galleryHint: {
    marginTop: 8,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
  },
  reminderHelp: {
    marginTop: 4, marginBottom: 6,
    fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft, lineHeight: 16,
  },
  suggestion: {
    marginVertical: 6,
    paddingHorizontal: 12, paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: 'rgba(225,163,61,0.14)',
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.32)',
  },
  suggestionEyebrow: {
    fontFamily: Fonts.sansBold, fontSize: 11,
    color: Palette.honeyDeep, letterSpacing: 0.7,
  },
  suggestionTitle: {
    marginTop: 2,
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink,
  },
  suggestionDate: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  clearReminder: { marginTop: 6, alignSelf: 'flex-start' },
  clearReminderText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.clay,
  },
  multiRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 12, paddingVertical: 10,
    borderRadius: 11,
    backgroundColor: Palette.sand,
    borderWidth: 1, borderColor: Palette.line,
    marginTop: 6,
  },
  multiLabel: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  multiDate: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  multiRemove: {
    width: 26, height: 26, borderRadius: 13,
    alignItems: 'center', justifyContent: 'center',
  },
  multiRemoveText: {
    fontFamily: Fonts.sansBold, fontSize: 18, color: Palette.clay, marginTop: -2,
  },
  addReminderBtn: {
    paddingHorizontal: 14, paddingVertical: 12, borderRadius: 11,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  addReminderText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13 },
});
